import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Scraper config — a fully declarative description of how to fetch a dealer/site
// listing over HTTP and turn its HTML into structured JSON records. Interpreted
// by the backend `ScraperToolExecutor`; no per-client code. One config lives in
// the `scrapers` table and can be assigned to one or more chatbot tools
// (tool_type='scraper').
// ─────────────────────────────────────────────────────────────────────────────

// Where a mapped request parameter goes.
export const scraperParamInSchema = z.enum(['query', 'path']);
export type ScraperParamIn = z.infer<typeof scraperParamInSchema>;

// One tool-argument → request-parameter mapping.
export const scraperParamMapEntrySchema = z.object({
  // Request parameter name (query key, or `{name}` path variable).
  param: z.string().min(1),
  in: scraperParamInSchema.optional().default('query'),
  // Optional label→value translation applied case-insensitively to the arg value
  // before it is sent (e.g. { diesel: 'D', petrol: 'P' }). If the arg already
  // equals one of the mapped VALUES it passes through unchanged; an unmapped
  // value passes through as-is unless `strict` is set (then it is dropped).
  value_map: z.record(z.string(), z.string()).optional(),
  strict: z.boolean().optional().default(false),
  // Send this fixed value when the arg is absent (rarely needed; fixed_params is
  // usually better).
  default: z.string().optional(),
});
export type ScraperParamMapEntry = z.infer<typeof scraperParamMapEntrySchema>;

export const scraperRequestSchema = z.object({
  base_url: z.string().url(),
  method: httpVerb().optional().default('GET'),
  // Optional URL fetched ONCE before the listing requests to establish a session
  // (its Set-Cookie response cookies are forwarded on every subsequent request).
  // Needed for sites whose listing/AJAX endpoint requires a prior page visit.
  bootstrap_url: z.string().url().optional(),
  // Static headers; values may contain {{env.NAME}} placeholders (resolved server-side).
  headers: z.record(z.string(), z.string()).optional(),
  // Always-sent query params (the constant part of the listing URL).
  fixed_params: z.record(z.string(), z.string()).optional(),
  // tool-arg name → how it becomes a request param.
  param_map: z.record(z.string(), scraperParamMapEntrySchema).optional(),
});
export type ScraperRequest = z.infer<typeof scraperRequestSchema>;

export const scraperPaginationSchema = z.object({
  // Query param carrying the page number.
  param: z.string().min(1),
  // First page value (usually 1, sometimes 0).
  start: z.number().int().optional().default(1),
  // Records per page — used to detect the last (short) page.
  page_size: z.number().int().positive(),
  // Hard cap on pages fetched per call (safety bound).
  max_pages: z.number().int().positive().max(100).optional().default(20),
});
export type ScraperPagination = z.infer<typeof scraperPaginationSchema>;

// How a single captured value is post-processed.
export const scraperTransformSchema = z.enum([
  'text', // strip tags + decode common entities + collapse whitespace
  'int', // keep digits, parseInt
  'float', // keep digits + '.', parseFloat
  'exists', // boolean: did the regex match at all
  'trim', // trim only
  'url_strip_query', // drop everything from '?' onward
]);
export type ScraperTransform = z.infer<typeof scraperTransformSchema>;

export const scraperFieldSchema = z.object({
  // Regex run against each record chunk; capture group 1 is the value (ignored
  // for transform 'exists', where only match/no-match matters).
  regex: z.string().min(1),
  // Regex flags (default 'i'). 's'/'m'/'g' allowed; 'g' is ignored (first match used).
  flags: z.string().optional(),
  transform: scraperTransformSchema.optional().default('text'),
  // Prepended to the final string value (e.g. 'V' for a stock id).
  prefix: z.string().optional(),
  // Fallback when the field's regex does not match.
  default: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
});
export type ScraperField = z.infer<typeof scraperFieldSchema>;

export const scraperRecordSelectorSchema = z.object({
  // Regex whose group 1 is one record's HTML chunk (run with the 'g' flag).
  regex: z.string().min(1),
  flags: z.string().optional(),
  // Only keep chunks whose HTML contains this substring (skips non-record <li>s).
  must_contain: z.string().optional(),
});
export type ScraperRecordSelector = z.infer<typeof scraperRecordSelectorSchema>;

export const scraperParseSchema = z.object({
  record_selector: scraperRecordSelectorSchema,
  fields: z.record(z.string(), scraperFieldSchema),
  // Field name whose value uniquely identifies a record (for cross-page dedupe).
  dedupe_by: z.string().optional(),
  // Drop a record if this field ends up empty/absent (e.g. require 'detail_url').
  require_field: z.string().optional(),
});
export type ScraperParse = z.infer<typeof scraperParseSchema>;

export const scraperFilterOpSchema = z.enum(['>=', '<=', '>', '<', '==', '!=', 'contains']);

// A post-parse filter: compare a parsed record `field` against the value of a
// tool argument `arg` (skipped when that arg is absent).
export const scraperClientFilterSchema = z.object({
  field: z.string().min(1),
  op: scraperFilterOpSchema,
  arg: z.string().min(1),
});
export type ScraperClientFilter = z.infer<typeof scraperClientFilterSchema>;

export const scraperConfigSchema = z.object({
  request: scraperRequestSchema,
  pagination: scraperPaginationSchema.optional(),
  parse: scraperParseSchema,
  client_filters: z.array(scraperClientFilterSchema).optional(),
  // Cap on total records returned (post-filter). Protects the context window.
  max_results: z.number().int().positive().optional().default(60),
});
export type ScraperConfig = z.infer<typeof scraperConfigSchema>;

// Local helper so this file has no import cycle with custom-tool.schema.
function httpVerb() {
  return z.enum(['GET', 'POST']);
}
